// @flow

import * as React from "react";
import cx from "classnames";
import { format } from "date-fns";

import inquiryStyles from "./InquirySummary.scss";
import sharedStyles from "../shared/Shared.scss";
import Card from "../../Card";
import LabeledContent from "../LabeledContent";
import Status from "../../Status";
import { timeDistanceFromDate } from "../../../utils/formatters";
import { datePickerDisplayFormat, timeFormat } from "../../../utils/formats";
import { OKP_PARTNER_NAME, getPartnerSourceLabel } from "../../../data/partners";

type InquirySummaryProps = {
  amount: number,
  banks: React.Node,
  company: string,
  createdAt: string,
  id: string,
  product: string,
  status: string,
  statusSite: "customer" | "buba" | "bank",
  isCustomerInquiry: boolean,
  phone: string,
  currencyFormatter: (amount: number) => string,
  documentsStatus: string,
  partnerName: string,
  inquiryLevel: string
};

const mapInquiryLevelText = new Map([
  ["okp_level1", "Einfache Voranfrage"],
  ["okp_level2", "Qualifizierte Anfrage"],
  ["okp_level3", "Antrag"]
]);

const InquirySummary = ({
  amount,
  banks,
  company,
  createdAt,
  id,
  product,
  status,
  statusSite,
  isCustomerInquiry,
  phone,
  currencyFormatter,
  documentsStatus,
  partnerName,
  inquiryLevel
}: InquirySummaryProps) => (
  <Card>
    <div className={sharedStyles.container} data-testid="inquirysummary">
      <div className={sharedStyles.row}>
        <div className={sharedStyles.col}>
          <LabeledContent label="ID">{id}</LabeledContent>
        </div>
        <div className={cx(sharedStyles.col, sharedStyles.borderRight)}>
          <LabeledContent label="Erstellt am" additionalContentStyles={inquiryStyles.muted}>
            {createdAt && (
              <>
                <div>{`${format(createdAt, datePickerDisplayFormat)}`}</div>
                <div>{`${format(createdAt, timeFormat)} Uhr`}</div>
              </>
            )}
          </LabeledContent>
        </div>
        <div className={sharedStyles.col}>
          <LabeledContent label="Unternehmen" additionalContentStyles={inquiryStyles.company}>
            {company}
          </LabeledContent>
        </div>
        <div className={cx(sharedStyles.col, sharedStyles.borderRight)}>
          {isCustomerInquiry ? (
            <LabeledContent label="Bank / Sparkasse">{banks}</LabeledContent>
          ) : (
            <LabeledContent label="Bank">{banks}</LabeledContent>
          )}
        </div>
        <div className={sharedStyles.col}>
          <LabeledContent label="Status">
            <Status site={statusSite} status={status} />
          </LabeledContent>
        </div>
      </div>
      <div className={sharedStyles.row}>
        <div className={cx(sharedStyles.col, sharedStyles.empty)}>
          {isCustomerInquiry ? (
            <LabeledContent label="Quelle">
              {partnerName ? getPartnerSourceLabel(partnerName) : "Eigene Anfrage"}
            </LabeledContent>
          ) : (
            <div />
          )}
        </div>
        <div className={cx(sharedStyles.col, sharedStyles.borderRight)}>
          <LabeledContent label="Alter" additionalContentStyles={inquiryStyles.muted}>
            {createdAt && timeDistanceFromDate(createdAt)}
          </LabeledContent>
        </div>
        <div className={sharedStyles.col}>
          <LabeledContent label="Volumen">
            {amount == null ? "-" : currencyFormatter(amount)}
          </LabeledContent>
        </div>
        <div className={cx(sharedStyles.col, sharedStyles.borderRight)}>
          {isCustomerInquiry ? (
            <LabeledContent label="Produkt">{product}</LabeledContent>
          ) : (
            <LabeledContent label="Telefon">{phone}</LabeledContent>
          )}
        </div>
        {isCustomerInquiry ? (
          <div className={cx(sharedStyles.col, sharedStyles.empty)} />
        ) : (
          <div className={sharedStyles.col}>
            <LabeledContent label="Eingangskanal">
              {partnerName === OKP_PARTNER_NAME ? "OKP" : "Bankanfrage"}
            </LabeledContent>
          </div>
        )}
      </div>
      {isCustomerInquiry ? null : (
        <div className={sharedStyles.row}>
          <div className={cx(sharedStyles.col, sharedStyles.empty)} />
          <div className={cx(sharedStyles.col, sharedStyles.borderRight)} />
          <div className={cx(sharedStyles.col, sharedStyles.empty)} />
          <div className={cx(sharedStyles.col, sharedStyles.borderRight)} />
          <div className={cx(sharedStyles.col)}>
            <LabeledContent label="Anfragelevel">
              {mapInquiryLevelText.get(inquiryLevel) || "-"}
            </LabeledContent>
          </div>
        </div>
      )}

      <div className={sharedStyles.row}>
        <div className={cx(sharedStyles.col, sharedStyles.empty)}>
          {isCustomerInquiry && ["new", "opened"].includes(status) && (
            <LabeledContent label="Dokumente">{documentsStatus}</LabeledContent>
          )}
        </div>
      </div>
    </div>
  </Card>
);

InquirySummary.defaultProps = {
  amount: 0,
  bankName: "",
  company: "",
  createdAt: "",
  id: "",
  product: "",
  status: ""
};

export default InquirySummary;
